import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../lib/api";
import { cn } from "../lib/cn";

interface AuditRow {
  id: number;
  at: string;
  actor?: string;
  action: string;
  target?: string;
  ip?: string;
  payload?: string;
}

interface AuditResponse {
  rows: AuditRow[];
  nextCursor?: number;
}

const PRESETS = [
  { label: "1h", hours: 1 },
  { label: "24h", hours: 24 },
  { label: "7d", hours: 24 * 7 },
];

const ACTION_TONE: Record<string, string> = {
  "auth.": "bg-brand-50 text-brand-700 border-brand-500/30",
  "user.": "bg-rose-50 text-rose-700 border-rose-500/30",
  "source.": "bg-emerald-50 text-emerald-700 border-emerald-500/30",
  "agent.": "bg-amber-50 text-amber-700 border-amber-500/30",
  "endpoint.": "bg-amber-50 text-amber-700 border-amber-500/30",
  "artifact.": "bg-amber-50 text-amber-700 border-amber-500/30",
};

export function AuditPage() {
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [sinceHours, setSinceHours] = useState<number | null>(24);
  const [pages, setPages] = useState<AuditRow[][]>([]);
  const [cursor, setCursor] = useState<number | undefined>(undefined);
  const [expanded, setExpanded] = useState<number | null>(null);

  const since = useMemo(() => {
    if (!sinceHours) return undefined;
    return new Date(Date.now() - sinceHours * 3_600_000).toISOString();
  }, [sinceHours]);

  const params = new URLSearchParams();
  if (actor) params.set("actor", actor);
  if (action) params.set("action", action);
  if (since) params.set("since", since);
  if (cursor) params.set("cursor", String(cursor));
  params.set("limit", "100");

  const audit = useQuery({
    queryKey: ["audit", actor, action, since, cursor],
    queryFn: () => api.get<AuditResponse>(`/api/v1/audit?${params.toString()}`),
  });

  const actions = useQuery({
    queryKey: ["audit", "actions"],
    queryFn: () => api.get<string[]>("/api/v1/audit/actions"),
    staleTime: 5 * 60_000,
  });

  const allRows = useMemo(() => {
    const rows = audit.data?.rows ?? [];
    return cursor ? [...pages.flat(), ...rows] : rows;
  }, [audit.data, pages, cursor]);

  function resetPagination() {
    setCursor(undefined);
    setPages([]);
  }

  return (
    <div className="page-container">
      {/* HEADER */}
      <header className="pt-8 pb-6">
        <div className="eyebrow mb-1">Compliance</div>
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-[32px] font-semibold leading-tight tracking-tight text-ink">
              Audit Log
            </h1>
            <p className="mt-1.5 max-w-xl text-[13.5px] leading-relaxed text-ash">
              Every state-changing action — login, source CRUD, endpoint
              changes, identity edits, user management. Admin-only.
            </p>
          </div>
          {allRows.length > 0 && (
            <div className="shrink-0 rounded-lg border border-rule bg-surface px-4 py-2 text-center">
              <div
                className="font-display text-[24px] font-semibold leading-none text-ink"
                data-num
              >
                {allRows.length}
              </div>
              <div className="mt-0.5 font-mono text-[9.5px] uppercase tracking-wider text-ash">
                events
              </div>
            </div>
          )}
        </div>
      </header>

      {/* FILTER BAR */}
      <section className="mb-5 card p-4">
        <div className="flex flex-wrap items-end gap-4">
          <Field label="Actor" className="w-40">
            <input
              value={actor}
              onChange={(e) => {
                setActor(e.target.value);
                resetPagination();
              }}
              placeholder="username"
              className="input py-1.5 text-[12.5px]"
            />
          </Field>
          <Field label="Action" className="w-48">
            <select
              value={action}
              onChange={(e) => {
                setAction(e.target.value);
                resetPagination();
              }}
              className="input py-1.5 text-[12.5px]"
            >
              <option value="">Any action</option>
              {actions.data?.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Time range">
            <div className="flex gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  onClick={() => {
                    setSinceHours(p.hours);
                    resetPagination();
                  }}
                  className={cn(
                    "rounded-md border px-3 py-1.5 text-[12px] font-medium",
                    sinceHours === p.hours
                      ? "border-litmus-dark-green-700 bg-litmus-dark-green-700 text-white"
                      : "border-rule bg-surface text-ash hover:border-ink/30 hover:text-ink",
                  )}
                >
                  {p.label}
                </button>
              ))}
              <button
                onClick={() => {
                  setSinceHours(null);
                  resetPagination();
                }}
                className={cn(
                  "rounded-md border px-3 py-1.5 text-[12px] font-medium",
                  sinceHours === null
                    ? "border-litmus-dark-green-700 bg-litmus-dark-green-700 text-white"
                    : "border-rule bg-surface text-ash hover:border-ink/30 hover:text-ink",
                )}
              >
                All time
              </button>
            </div>
          </Field>
        </div>
      </section>

      {/* TABLE */}
      <section className="card overflow-hidden">
        {audit.isLoading && pages.length === 0 && (
          <div className="space-y-0 divide-y divide-rule">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-12 animate-pulseSoft bg-datahub-gray-1500/50"
              />
            ))}
          </div>
        )}
        {allRows.length === 0 && !audit.isLoading && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <svg
              className="mb-3 h-7 w-7 text-ash/40"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M9 12h6M9 16h4M7 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <rect
                x="9"
                y="2"
                width="6"
                height="4"
                rx="1"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>
            <p className="text-[13px] text-ash">No matching audit events.</p>
          </div>
        )}
        {allRows.length > 0 && (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-rule bg-datahub-gray-1500 text-left">
                <Th>When</Th>
                <Th>Actor</Th>
                <Th>Action</Th>
                <Th>Target</Th>
                <Th>IP</Th>
                <Th>Payload</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rule">
              {allRows.map((row) => {
                const open = expanded === row.id;
                return (
                  <>
                    <tr
                      key={row.id}
                      onClick={() => setExpanded(open ? null : row.id)}
                      className={cn(
                        "cursor-pointer transition-colors hover:bg-paper",
                        open && "bg-paper",
                      )}
                    >
                      <Td>
                        <span className="whitespace-nowrap font-mono text-[11.5px] text-ink">
                          {new Date(row.at).toLocaleString()}
                        </span>
                      </Td>
                      <Td>
                        {row.actor ? (
                          <span className="font-mono text-[12px] text-ink">
                            {row.actor}
                          </span>
                        ) : (
                          <span className="text-ash/60">system</span>
                        )}
                      </Td>
                      <Td>
                        <ActionChip action={row.action} />
                      </Td>
                      <Td>
                        {row.target ? (
                          <span className="font-mono text-[11px] text-ink">
                            {row.target.slice(0, 18)}
                            {row.target.length > 18 ? "…" : ""}
                          </span>
                        ) : (
                          <span className="text-ash/40">—</span>
                        )}
                      </Td>
                      <Td>
                        <span className="font-mono text-[11px] text-ash">
                          {row.ip ?? "—"}
                        </span>
                      </Td>
                      <Td>
                        {row.payload ? (
                          <span className="line-clamp-1 max-w-[220px] truncate text-[11.5px] text-ash">
                            {row.payload}
                          </span>
                        ) : (
                          <span className="text-ash/40">—</span>
                        )}
                      </Td>
                    </tr>
                    {open && row.payload && (
                      <tr
                        key={`${row.id}-detail`}
                        className="bg-datahub-gray-1500"
                      >
                        <td colSpan={6} className="px-5 py-3">
                          <pre className="overflow-x-auto rounded-md border border-rule bg-surface p-3 font-mono text-[11px] leading-relaxed text-ink">
                            {prettyJSON(row.payload)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {audit.data?.nextCursor && (
        <div className="mt-5 flex justify-center">
          <button
            onClick={() => {
              if (audit.data?.rows) setPages((p) => [...p, audit.data!.rows]);
              setCursor(audit.data?.nextCursor);
            }}
            className="btn-secondary"
          >
            Load older →
          </button>
        </div>
      )}
    </div>
  );
}

function ActionChip({ action }: { action: string }) {
  const tone =
    Object.entries(ACTION_TONE).find(([prefix]) =>
      action.startsWith(prefix),
    )?.[1] ?? "bg-paper text-ash border-rule";
  return (
    <span
      className={cn(
        "rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider",
        tone,
      )}
    >
      {action}
    </span>
  );
}

function prettyJSON(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={cn("block", className)}>
      <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-ash">
        {label}
      </span>
      {children}
    </label>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return (
    <th className="px-5 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-ash">
      {children}
    </th>
  );
}

function Td({ children }: { children?: React.ReactNode }) {
  return <td className="px-5 py-3 align-middle">{children}</td>;
}
